/* eslint-disable */
import React, {useEffect, useRef, useState} from "react";
import {FilePond, registerPlugin} from "react-filepond";
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileMetadata from 'filepond-plugin-file-metadata';
import FilePondPluginFileRename from 'filepond-plugin-file-rename';
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageResize from 'filepond-plugin-image-resize';
import {fixUploaderTextOnFinish, onCheckIfCanClearList, onCheckIfCanUpload, onCheckIfQueueHasErrors, onClearFileList, onHandleInit, onHandleUpload, sanitizeString, setCustomMsg} from "@/utils/filepond";
import {errorMessageFromServer} from "@/utils/server";
import {toastError} from "@/helpers/toast";
import {Box, Stack, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {base64Config, urlConfig} from "@/components/forms/Uploader/config";
import FormLabelPond from "@/components/forms/Uploader/components/FormLabelPond";
import {styled} from "@mui/material/styles";
import ButtonsPond from "@/components/forms/Uploader/components/ButtonsPond";
import {Field} from "react-final-form";

// Register the plugins
registerPlugin(
    FilePondPluginFileValidateType,
    FilePondPluginFileValidateSize,
    FilePondPluginFileMetadata,
    FilePondPluginFileRename,
    FilePondPluginImageValidateSize,
    FilePondPluginImagePreview,
    FilePondPluginImageResize
);

const BoxUploaderStyle = styled(Box, {
    shouldForwardProp: (prop) => prop !== "hasUploadButton" && prop !== "hasResetButton"
})<{ hasUploadButton?: boolean; hasResetButton?: boolean }>(({theme, hasUploadButton, hasResetButton}) => ({
    minWidth: '100%',
    marginLeft: theme.spacing(0),
    ...((hasUploadButton || hasResetButton) && {
        [theme.breakpoints.up('sm')]: {
            minWidth: 'calc(100% - 104px)',
            marginLeft: theme.spacing(2),
        }
    })
}));

const BoxButtonsStyle = styled(Box, {
    shouldForwardProp: (prop) => prop !== "hasUploadButton" && prop !== "hasResetButton"
})<{ hasUploadButton?: boolean; hasResetButton?: boolean }>(({theme, hasUploadButton, hasResetButton}) => ({
    ...((hasUploadButton || hasResetButton) && {
        minWidth: '88px',
        marginLeft: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            minWidth: '100%',
            marginLeft: theme.spacing(0),
        }
    })
}));

const ErrorStyle = styled(Typography)(({theme}) => ({
    color: theme.palette.error.main
}));

type FilePondProps = {
    name: string;
    mainUrl: string;
    maxFiles: number;
    allowMultiple?: boolean;
    instantUpload?: boolean;
    authorization?: string;
    formDataHeader?: any
    onInit?: () => void;
    onInitWrapper?: () => void;
    onProcessComplete?: (arg: any) => void;
    onProcessCompleteWrapper?: () => void;
    handleChangeForm?: (arg1: any, arg2: any) => void;
    onErrorFile?: (arg1: any, arg2: any) => void;
    onAddFile?: (arg1: any, arg2: any) => void;
    onRemoveFile?: (arg?: any) => void;
    isBase64?: boolean;
    base64Callback?: (arg1: any, arg2: any, arg3?: any) => void;
    fileValidateTypeDetectTypeCustom?: (file: any, source: string, acceptedFileTypes: string[]) => Promise<string>;
    allowFileTypeValidation?: boolean;
    fileValidateTypeLabelExpectedTypesMap?: object;
    acceptedFileTypes?: string[];
    maxFileSize?: string;
    allowFileSizeValidation?: boolean;
    allowImageValidateSize?: boolean;
    imageValidateSizeMinWidth?: number;
    imageValidateSizeMaxWidth?: number;
    imageValidateSizeMinHeight?: number;
    imageValidateSizeMaxHeight?: number;
    allowFileMetadata?: boolean;
    fileMetadataObject?: object;
    label?: string;
    translateLabels?: boolean;
    requiredFile?: boolean;
    hasUploadButton?: boolean;
    hasResetButton?: boolean;
    hasError?: boolean,
    isTouched?: boolean;
    formFieldName?: string;
    formExtra?: string,
    required?: any;
    imageResizeTargetWidth?: number | any;
    imageResizeTargetHeight?: number | any;
}

export const FilepondWrapper = (props: FilePondProps): any => {
    const {
        name,
        mainUrl,
        maxFiles, allowMultiple, instantUpload,
        authorization, formDataHeader,
        onInit, onInitWrapper,
        onProcessComplete, onProcessCompleteWrapper,
        handleChangeForm,
        onErrorFile,
        onAddFile,
        onRemoveFile,
        isBase64, base64Callback,
        allowFileTypeValidation, fileValidateTypeLabelExpectedTypesMap, acceptedFileTypes, fileValidateTypeDetectTypeCustom,
        allowFileSizeValidation, maxFileSize,
        allowImageValidateSize, imageValidateSizeMinWidth, imageValidateSizeMaxWidth, imageValidateSizeMinHeight, imageValidateSizeMaxHeight,
        allowFileMetadata, fileMetadataObject,
        label, translateLabels, requiredFile,
        hasUploadButton, hasResetButton,
        hasError, isTouched,
        formFieldName, formExtra,
        required,
        imageResizeTargetWidth, imageResizeTargetHeight
    } = props;

    const {i18n} = useTranslation(['filepond'])

    const [files, setFiles] = useState([]);
    const [filesHasError, setFilesHasError] = useState(false);
    const [isFilepondInit, setIsFilepondInit] = useState(false);
    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    const [serverError, setServerError] = useState(null);

    // la referencia al file pond
    const pond = useRef();

    useEffect(() => {
        console.log('language change filepond');
    }, [i18n.language])

    const onServerError = (error: unknown): any => {
        console.log('on server error', error);

        // para utilizarlo después
        setServerError(error);

        // print simple error on interface
        return i18n.t('filepond.labelFileProcessingError');
    }

    const onServerData = (formData: FormData): FormData => {
        Object.keys(formDataHeader).forEach(function (key: string) {
            formData.append(key, formDataHeader[key]);
        });
        return formData;
    }

    const notify = (filename: string, msg: any): void => {
        // generar notificador
        const errorFragment = errorMessageFromServer(msg);
        toastError(errorFragment);
    };

    const handleUpload = (): void => {
        const startUpload = onHandleUpload(pond.current); // viene desde el helper
        setIsProcessingFiles(startUpload);
    };

    const clearFileList = (): void => {
        const clearData = onClearFileList(pond.current);

        setFiles(clearData.files);
        setIsProcessingFiles(clearData.isProcessingFiles);
        setFilesHasError(clearData.filesHasError);
    };

    const disableUpload = !onCheckIfCanUpload(pond.current, files, filesHasError, isProcessingFiles);
    const disableReset = !onCheckIfCanClearList(files, isProcessingFiles);

    const displayError = (hasError !== undefined && Array.isArray(hasError));

    // create configurations for server
    const serverConfig: any = isBase64 ? base64Config(name, base64Callback) : urlConfig(mainUrl, onServerError, onServerData);

    return (
        <Stack>
            {
                isFilepondInit &&
                <FormLabelPond
                    label={label}
                    translateLabels={translateLabels}
                    requiredFile={required}
                    displayError={hasError && isTouched && displayError}
                    formExtra={formExtra}
                />
            }

            {
                (hasError && isTouched && displayError) &&
                <ErrorStyle variant={'caption'} sx={{ml: 2}}>
                    {i18n.t('validations.required')}
                </ErrorStyle>
            }

            <Field
                name={`${formFieldName}`}
                required={required}
                render={({field}) => <input type={"hidden"} {...field} />}
            />

            <Stack direction={{xs: 'column', sm: 'row'}} alignItems="flex-start" spacing={2}>
                <BoxUploaderStyle
                    hasUploadButton={hasUploadButton}
                    hasResetButton={hasResetButton}
                >
                    <FilePond
                        ref={pond}
                        name={name}
                        maxFiles={maxFiles}
                        files={files}

                        allowDrop={true}
                        allowBrowse={true}
                        allowPaste={false}
                        allowMultiple={allowMultiple}

                        instantUpload={instantUpload}

                        chunkUploads={true}
                        chunkForce={true}
                        chunkSize={1000000}
                        chunkRetryDelays={null}
                        maxParallelUploads={1}

                        server={{
                            url: serverConfig.url,
                            process: serverConfig.process,
                            revert: serverConfig.revert,
                            restore: serverConfig.restore,
                            load: serverConfig.load,
                            fetch: serverConfig.fetch,
                            headers: {
                                'type': 'uploader',
                                'Authorization': authorization,
                                'form-data-header': unescape(encodeURIComponent(JSON.stringify(formDataHeader)))
                            },
                        }}

                        labelIdle={i18n.t('filepond.labelIdle', {interpolation: {escapeValue: false}})}
                        labelInvalidField={i18n.t('filepond.labelInvalidField')}
                        labelFileWaitingForSize={i18n.t('filepond.labelFileWaitingForSize')}
                        labelFileSizeNotAvailable={i18n.t('filepond.labelFileSizeNotAvailable')}
                        labelFileLoading={i18n.t('filepond.labelFileLoading')}
                        labelFileLoadError={i18n.t('filepond.labelFileLoadError')}
                        labelFileProcessing={(isBase64) ? i18n.t('filepond.labelFileProcessingBase64') : i18n.t('filepond.labelFileProcessing')}
                        labelFileProcessingComplete={(isBase64) ? i18n.t('filepond.labelFileProcessingCompleteBase64') : i18n.t('filepond.labelFileProcessingComplete')}
                        labelFileProcessingAborted={(isBase64) ? i18n.t('filepond.labelFileProcessingAbortedBase64') : i18n.t('filepond.labelFileProcessingAborted')}
                        labelFileProcessingError={(isBase64) ? i18n.t('filepond.labelFileProcessingErrorBase64') : i18n.t('filepond.labelFileProcessingError')}
                        labelFileProcessingRevertError={i18n.t('filepond.labelFileProcessingRevertError')}
                        labelFileRemoveError={i18n.t('filepond.labelFileRemoveError')}
                        labelTapToCancel={i18n.t('filepond.labelTapToCancel')}
                        labelTapToRetry={i18n.t('filepond.labelTapToRetry')}
                        labelTapToUndo={i18n.t('filepond.labelTapToUndo')}
                        labelButtonRemoveItem={i18n.t('filepond.labelButtonRemoveItem')}
                        labelButtonAbortItemLoad={i18n.t('filepond.labelButtonAbortItemLoad')}
                        labelButtonRetryItemLoad={i18n.t('filepond.labelButtonRetryItemLoad')}
                        labelButtonAbortItemProcessing={i18n.t('filepond.labelButtonAbortItemProcessing')}
                        labelButtonUndoItemProcessing={i18n.t('filepond.labelButtonUndoItemProcessing')}
                        labelButtonRetryItemProcessing={i18n.t('filepond.labelButtonRetryItemProcessing')}
                        labelButtonProcessItem={(isBase64) ? i18n.t('filepond.labelButtonProcessItemBase64') : i18n.t('filepond.labelButtonProcessItem')}

                        // rename options
                        allowFileRename={true}
                        fileRenameFunction={
                            (file) => {
                                const newFileName = sanitizeString(file.basename);
                                return `${newFileName}${file.extension}`;
                            }
                        }

                        oninit={() => {
                            // initialize pond
                            const init = onHandleInit(onInit, pond.current)

                            if (onInitWrapper)
                                onInitWrapper();

                            setIsFilepondInit(init);
                        }}

                        onprocessfile={(error, file) => {
                            if (error)
                                setCustomMsg(error, file.id);

                            fixUploaderTextOnFinish(file.id);
                        }}

                        onprocessfilestart={() => {

                            // limpiar para reutilizar
                            setServerError(null);

                            if (instantUpload)
                                setIsProcessingFiles(true);
                        }}

                        onaddfilestart={() => {
                            if (instantUpload)
                                setIsProcessingFiles(true);
                        }}

                        onprocessfiles={() => {
                            // llamada al método del parent
                            if (onProcessComplete)
                                onProcessComplete(pond.current);

                            if (onProcessCompleteWrapper)
                                onProcessCompleteWrapper();

                            setIsProcessingFiles(false);
                        }}

                        onupdatefiles={fileItems => {
                            // notificar al padre
                            if (handleChangeForm) {
                                if (fileItems.length > 0)
                                    handleChangeForm(name, (onCheckIfQueueHasErrors(fileItems)) ? '' : fileItems);
                                else
                                    handleChangeForm(name, '');
                            }

                            // Set currently active file objects to state
                            setFiles(fileItems.map(fileItem => {
                                return fileItem.file;
                            }));

                            setFilesHasError(onCheckIfQueueHasErrors(fileItems));
                        }}

                        onaddfile={(error, file) => {
                            // testing notifications
                            //notify(file.filename, <>test</>)

                            if (error && onErrorFile) {
                                onErrorFile(error, file);
                            } else {
                                if (onAddFile)
                                    onAddFile(file.file, pond.current);
                            }
                        }}

                        onremovefile={(error, file) => {
                            if (error) {
                                if (onErrorFile) {
                                    onErrorFile(error, file.file);
                                }
                            }

                            if (onRemoveFile) {
                                onRemoveFile(file.file);
                            }
                        }}

                        onerror={(error, file) => {
                            if (error && error.code === 422) {
                                // call notifier
                                notify(file.filename, serverError);

                                // limpiar para reutilizar
                                setServerError(null);
                            }

                            // reset processing
                            setIsProcessingFiles(false);
                        }}

                        // validation type
                        allowFileTypeValidation={allowFileTypeValidation}
                        acceptedFileTypes={acceptedFileTypes}
                        labelFileTypeNotAllowed={i18n.t('filepond.labelFileTypeNotAllowed')}
                        fileValidateTypeLabelExpectedTypes={i18n.t('filepond.fileValidateTypeLabelExpectedTypes')}
                        fileValidateTypeLabelExpectedTypesMap={fileValidateTypeLabelExpectedTypesMap}
                        fileValidateTypeDetectType={(file, source) => {
                            if (fileValidateTypeDetectTypeCustom)
                                return fileValidateTypeDetectTypeCustom(file, source, acceptedFileTypes);

                            return new Promise((resolve) => resolve(source));
                        }}

                        // validation size
                        allowFileSizeValidation={allowFileSizeValidation}
                        maxFileSize={maxFileSize}
                        labelMaxFileSizeExceeded={i18n.t('filepond.labelMaxFileSizeExceeded')}
                        labelMaxFileSize={i18n.t('filepond.labelMaxFileSize')}

                        // validation image
                        allowImageValidateSize={allowImageValidateSize}
                        imageValidateSizeMinWidth={imageValidateSizeMinWidth}
                        imageValidateSizeMaxWidth={imageValidateSizeMaxWidth}
                        imageValidateSizeMinHeight={imageValidateSizeMinHeight}
                        imageValidateSizeMaxHeight={imageValidateSizeMaxHeight}
                        imageValidateSizeLabelFormatError={i18n.t('filepond.imageValidateSizeLabelFormatError')}
                        imageValidateSizeLabelImageSizeTooSmall={i18n.t('filepond.imageValidateSizeLabelImageSizeTooSmall')}
                        imageValidateSizeLabelImageSizeTooBig={i18n.t('filepond.imageValidateSizeLabelImageSizeTooBig')}
                        imageValidateSizeLabelExpectedMinSize={i18n.t('filepond.imageValidateSizeLabelExpectedMinSize')}
                        imageValidateSizeLabelImageResolutionTooLow={i18n.t('filepond.imageValidateSizeLabelImageResolutionTooLow')}
                        imageValidateSizeLabelImageResolutionTooHigh={i18n.t('filepond.imageValidateSizeLabelImageResolutionTooHigh')}
                        imageValidateSizeLabelExpectedMinResolution={i18n.t('filepond.imageValidateSizeLabelExpectedMinResolution')}
                        imageValidateSizeLabelExpectedMaxResolution={i18n.t('filepond.imageValidateSizeLabelExpectedMaxResolution')}

                        allowFileMetadata={allowFileMetadata}
                        fileMetadataObject={fileMetadataObject}

                        //image resize
                        allowImageResize={true}
                        imageResizeUpscale={true}
                        imageResizeTargetWidth={imageResizeTargetWidth || null}
                        imageResizeTargetHeight={imageResizeTargetHeight || null}
                    />
                </BoxUploaderStyle>

                <BoxButtonsStyle
                    hasUploadButton={hasUploadButton}
                    hasResetButton={hasResetButton}
                    style={{marginTop: 0}}
                >
                    <ButtonsPond
                        handleUpload={handleUpload}
                        clearFileList={clearFileList}
                        hasUploadButton={hasUploadButton}
                        hasResetButton={hasResetButton}
                        disableUpload={disableUpload}
                        disableReset={disableReset}
                    />
                </BoxButtonsStyle>
            </Stack>
        </Stack>
    );
}
